import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectLensEditorForm,
  selectLensEditorLensId,
  selectProfileBuilderFieldById,
  updateFormField,
} from "redux/slices/lensEditor";
import FormFieldEditor from "./FormFieldEditor";
import { EditableFormField } from "Backend";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useContext } from "react";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";

const ProfileBuilderFieldSettings = () => {
  const form = useAppSelector(selectLensEditorForm);
  // Move to ID, so the state gets refreshed automatically
  const { sectionId, fieldId } = useParamNumbers(["sectionId", "fieldId"]);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const selectedField = useAppSelector((state) => selectProfileBuilderFieldById(state, fieldId));
  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const updateField = (field: EditableFormField) => {
    if (selectedField) {
      dispatch(
        updateFormField({
          id: selectedField.id,
          values: {
            exportExclude: field.exportExclude !== selectedField.exportExclude ? field.exportExclude : undefined,
            key: field.key !== selectedField.key ? field.key : undefined,
            name: field.name !== selectedField.name ? field.name : undefined,
            prompt: field.prompt !== selectedField.prompt ? field.prompt : undefined,
            promptYesNo: field.promptYesNo !== selectedField.promptYesNo ? field.promptYesNo : undefined,
            repeating: field.repeating !== selectedField.repeating ? field.repeating : undefined,
            rank: field.rank !== selectedField.rank ? field.rank : undefined,
            required: field.required !== selectedField.required ? field.required : undefined,
            sectionId: field.sectionId && field.sectionId !== selectedField.sectionId ? field.sectionId : undefined,
            reviewConfigId:
              field.reviewConfigId && field.reviewConfigId !== selectedField.reviewConfigId
                ? field.reviewConfigId
                : undefined,
            description: field.description !== selectedField.description ? field.description : undefined,
            groupByCellId: field.groupByCellId !== selectedField.groupByCellId ? field.groupByCellId : undefined,
            analyticsExclude:
              field.analyticsExclude !== selectedField.analyticsExclude ? field.analyticsExclude : undefined,
          },
          clears: {
            ...(!field.key ? { key: true } : {}),
            ...(field.groupByCellId === undefined ? { groupByCellId: true } : {}),
          },
        })
      )
        .unwrap()
        .then(() => {
          enqueueSnackbar("Field updated", {
            variant: "success",
          });
          enqueueLensUpdateTimerSnackbar(lensId);
        })
        .catch((gqlError) => {
          enqueueSnackbar(`Unable to update field - ${gqlError?.message}`, {
            variant: "error",
          });
        });
    }
  };

  if (!selectedField || fieldId === undefined || sectionId === undefined) {
    return <Navigate to="sections" />;
  }

  return (
    <FormFieldEditor
      isLive={form.live}
      field={selectedField}
      rank={selectedField.rank}
      onSave={updateField}
      defaultSectionId={sectionId}
    />
  );
};

export default ProfileBuilderFieldSettings;
